import React, { useState, useEffect, useRef } from 'react';
import { keyframes } from '@emotion/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import StopIcon from '@mui/icons-material/Stop';
import SendIcon from '@mui/icons-material/Send';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import HeadphonesIcon from '@mui/icons-material/Headphones';
import { useAudioRecorder } from '../hooks/useAudioRecorder';
import { submitVoiceSubmission, ApiError } from '../api';

// ── Keyframes ──────────────────────────────────────────────────────────────
const barBounce = keyframes`
  0%, 100% { transform: scaleY(0.2); opacity: 0.5; }
  50%       { transform: scaleY(1);   opacity: 1;   }
`;

const blink = keyframes`
  0%, 49% { opacity: 1; }
  50%, 100% { opacity: 0; }
`;

// ── Constants ──────────────────────────────────────────────────────────────
const BAR_COUNT = 9;
const BAR_DELAYS  = [0, 0.12, 0.06, 0.18, 0.03, 0.15, 0.09, 0.21, 0.0];
const BAR_HEIGHTS = [18, 30, 36, 28, 40, 24, 34, 20, 28];
const BAR_SPEEDS  = [0.72, 0.80, 0.68, 0.88, 0.74, 0.82, 0.70, 0.78, 0.84];

// ── Styles ─────────────────────────────────────────────────────────────────
const shellSx = {
  minHeight: '100dvh',
  background: 'linear-gradient(180deg, #0a1628 0%, #0f2040 40%, #162d58 100%)',
  display: 'flex',
  alignItems: { xs: 'stretch', sm: 'center' },
  justifyContent: 'center',
  py: { xs: 2, sm: 4 },
  pb: { xs: 10, sm: 12 },
};

const pageStackSx = {
  width: '100%',
  maxWidth: 560,
  mx: 'auto',
  px: { xs: 2, sm: 3 },
};

const paperSx = {
  p: { xs: 2, sm: 3 },
  borderColor: 'rgba(255,255,255,0.1)',
  bgcolor: 'rgba(255,255,255,0.04)',
};

const outlinedLight = {
  borderColor: 'rgba(255,255,255,0.3)',
  color: '#fff',
};

const controlRowBtn = {
  minHeight: 44,
  flex: { xs: '1 1 auto', sm: '0 0 auto' },
};

// ── Helpers ────────────────────────────────────────────────────────────────
function blobToUploadFile(blob) {
  const ext = blob.type.includes('webm') ? 'webm' : blob.type.includes('mp4') ? 'm4a' : 'audio';
  return new File([blob], `voice-${Date.now()}.${ext}`, { type: blob.type || 'audio/webm' });
}

function formatTime(totalSeconds) {
  const s = Math.max(0, Math.floor(totalSeconds));
  const m = Math.floor(s / 60).toString().padStart(2, '0');
  const sec = (s % 60).toString().padStart(2, '0');
  return `${m}:${sec}`;
}

// ── RecordingAnimation ─────────────────────────────────────────────────────
function RecordingAnimation({ elapsed, maxSeconds }) {
  const showCap = typeof maxSeconds === 'number' && maxSeconds > 0;
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: 1.5,
        py: 2,
        px: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          height: 52,
        }}
      >
        {Array.from({ length: BAR_COUNT }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 5,
              height: `${BAR_HEIGHTS[i]}px`,
              bgcolor: '#f44336',
              borderRadius: '3px',
              transformOrigin: 'center',
              animation: `${barBounce} ${BAR_SPEEDS[i]}s ease-in-out ${BAR_DELAYS[i]}s infinite`,
            }}
          />
        ))}
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box
          sx={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            bgcolor: '#f44336',
            animation: `${blink} 1s step-end infinite`,
          }}
        />
        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.85)',
            fontVariantNumeric: 'tabular-nums',
            fontSize: '0.9rem',
            fontWeight: 700,
            letterSpacing: '0.08em',
          }}
        >
          {showCap ? `${formatTime(elapsed)} / ${formatTime(maxSeconds)}` : formatTime(elapsed)}
        </Typography>
      </Box>
    </Box>
  );
}

// ── AudioPlayer ────────────────────────────────────────────────────────────
function AudioPlayer({ blob }) {
  const { t } = useTranslation();
  const audioRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);

  useEffect(() => {
    const url = URL.createObjectURL(blob);
    const el = audioRef.current;
    if (el) el.src = url;
    return () => {
      if (el) el.src = '';
      URL.revokeObjectURL(url);
    };
  }, [blob]);

  function togglePlay() {
    const el = audioRef.current;
    if (!el) return;
    if (playing) {
      el.pause();
    } else {
      el.play().catch(() => {});
    }
  }

  function handleSeek(e) {
    if (!audioRef.current || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    audioRef.current.currentTime = ratio * duration;
  }

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;

  return (
    <Box
      sx={{
        bgcolor: 'rgba(79,195,247,0.08)',
        border: '1px solid rgba(79,195,247,0.2)',
        borderRadius: 2,
        p: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <HeadphonesIcon sx={{ color: '#4fc3f7', fontSize: 16 }} />
        <Typography variant="caption" sx={{ color: '#4fc3f7', fontWeight: 600, fontSize: '0.75rem' }}>
          {t('voiceFeedback.listenTitle')}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <IconButton
          onClick={togglePlay}
          size="small"
          aria-label={playing ? t('voiceFeedback.pause') : t('voiceFeedback.play')}
          sx={{
            bgcolor: '#4fc3f7',
            color: '#0a1628',
            width: 36,
            height: 36,
            flexShrink: 0,
            '&:hover': { bgcolor: '#81d4fa' },
          }}
        >
          {playing ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </IconButton>

        <Box
          onClick={handleSeek}
          sx={{
            flex: 1,
            height: 20,
            display: 'flex',
            alignItems: 'center',
            cursor: 'pointer',
          }}
        >
          <Box
            sx={{
              width: '100%',
              height: 4,
              bgcolor: 'rgba(255,255,255,0.15)',
              borderRadius: 2,
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                height: '100%',
                width: `${progress}%`,
                bgcolor: '#4fc3f7',
                borderRadius: 2,
                transition: 'width 0.1s linear',
              }}
            />
          </Box>
        </Box>

        <Typography
          variant="caption"
          sx={{
            color: 'rgba(255,255,255,0.6)',
            fontVariantNumeric: 'tabular-nums',
            fontSize: '0.72rem',
            minWidth: 68,
            textAlign: 'right',
          }}
        >
          {formatTime(currentTime)} / {formatTime(duration)}
        </Typography>
      </Box>

      <audio
        ref={audioRef}
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => {
          setPlaying(false);
          setCurrentTime(0);
          if (audioRef.current) audioRef.current.currentTime = 0;
        }}
        onTimeUpdate={() => audioRef.current && setCurrentTime(audioRef.current.currentTime)}
        onLoadedMetadata={() => audioRef.current && setDuration(audioRef.current.duration)}
        style={{ display: 'none' }}
      />
    </Box>
  );
}

// ── Config ──────────────────────────────────────────────────────────────────
const MAX_RECORDING_SECONDS = (() => {
  const raw = process.env.REACT_APP_VOICE_MAX_RECORDING_SECONDS;
  if (!raw) return 90;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : 90;
})();

function formatMaxDuration(seconds) {
  if (seconds >= 60 && seconds % 60 === 0) return `${seconds / 60} min`;
  if (seconds >= 60) return `${Math.floor(seconds / 60)} min ${seconds % 60} s`;
  return `${seconds} s`;
}

// ── Page ───────────────────────────────────────────────────────────────────
export default function VoiceFeedbackPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const stopId = searchParams.get('stopId');

  const { phase, blob, error, hitMaxDuration, start, stop, reset } = useAudioRecorder({
    maxDurationSeconds: MAX_RECORDING_SECONDS,
  });
  const [submitPhase, setSubmitPhase] = useState('idle');
  const [submitError, setSubmitError] = useState('');
  const [submitInfo, setSubmitInfo] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [isConsentOpen, setIsConsentOpen] = useState(false);

  const isRecording = phase === 'recording';
  const isBusy = phase === 'finalizing' || submitPhase === 'loading';
  const canSubmit = phase === 'ready' && blob && blob.size > 0 && submitPhase !== 'success';

  // Live recording timer
  useEffect(() => {
    if (!isRecording) {
      setElapsed(0);
      return;
    }
    setElapsed(0);
    const id = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [isRecording]);

  async function submitVoiceMessage() {
    if (!blob || blob.size === 0) {
      setSubmitError(t('voiceFeedback.errors.needRecording'));
      return;
    }
    setSubmitInfo('');
    setSubmitError('');
    setSubmitPhase('loading');
    try {
      await submitVoiceSubmission(blobToUploadFile(blob), stopId);
      setSubmitPhase('success');
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.message
          : t('voiceFeedback.errors.submitFailed');
      setSubmitError(msg);
      setSubmitPhase('error');
    }
  }

  function handleSubmit() {
    if (!blob || blob.size === 0) {
      setSubmitError(t('voiceFeedback.errors.needRecording'));
      return;
    }
    setSubmitError('');
    setSubmitInfo('');
    setIsConsentOpen(true);
  }

  async function handleConsentAccept() {
    setIsConsentOpen(false);
    await submitVoiceMessage();
  }

  function handleConsentDecline() {
    setIsConsentOpen(false);
    setSubmitInfo(t('voiceFeedback.aiConsent.notSent'));
  }

  function handleNewMessage() {
    reset();
    setSubmitPhase('idle');
    setSubmitError('');
    setSubmitInfo('');
    setElapsed(0);
    setIsConsentOpen(false);
  }

  return (
    <Box sx={shellSx}>
      <Stack spacing={3} sx={pageStackSx}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => navigate('/mobilite')}
            sx={{ color: '#4fc3f7', p: 0.5 }}
            aria-label={t('nav.mobility')}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography fontWeight={700} fontSize={14} color="rgba(255,255,255,0.7)">
            {t('nav.mobility')}
          </Typography>
        </Box>

        <Paper variant="outlined" sx={paperSx}>
          <Stack spacing={2}>
            <Typography
              variant="h6"
              fontWeight={800}
              color="#fff"
              sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
            >
              {t('voiceFeedback.title')}
            </Typography>
            <Typography variant="body2" color="rgba(255,255,255,0.65)" sx={{ lineHeight: 1.6 }}>
              {t('voiceFeedback.subtitle')}
            </Typography>
            <Typography variant="caption" color="rgba(255,255,255,0.45)" sx={{ display: 'block' }}>
              {t('voiceFeedback.maxDurationHint', { duration: formatMaxDuration(MAX_RECORDING_SECONDS) })}
            </Typography>

            {error === 'micDenied' && (
              <Alert severity="warning">{t('voiceFeedback.errors.micDenied')}</Alert>
            )}

            {submitPhase === 'success' ? (
              <>
                <Alert severity="success">{t('voiceFeedback.successTitle')}</Alert>
                <Typography variant="body2" color="rgba(255,255,255,0.75)">
                  {t('voiceFeedback.successDesc')}
                </Typography>
                <Button
                  variant="outlined"
                  onClick={handleNewMessage}
                  sx={{ ...outlinedLight, width: { xs: '100%', sm: 'auto' } }}
                >
                  {t('voiceFeedback.newRecording')}
                </Button>
              </>
            ) : (
              <>
                {isRecording && (
                  <RecordingAnimation elapsed={elapsed} maxSeconds={MAX_RECORDING_SECONDS} />
                )}

                {phase === 'ready' && hitMaxDuration && (
                  <Alert severity="info">{t('voiceFeedback.maxDurationReached')}</Alert>
                )}

                {phase === 'ready' && blob && blob.size > 0 && submitPhase !== 'loading' && (
                  <AudioPlayer blob={blob} />
                )}

                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1.5}
                  alignItems={{ xs: 'stretch', sm: 'center' }}
                >
                  <Stack
                    direction="row"
                    flexWrap="wrap"
                    useFlexGap
                    spacing={1.5}
                    sx={{ flex: { sm: 1 }, minWidth: 0 }}
                  >
                    {!isRecording && phase !== 'finalizing' && (
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<FiberManualRecordIcon />}
                        onClick={() => start()}
                        disabled={isBusy}
                        sx={controlRowBtn}
                      >
                        {t('voiceFeedback.record')}
                      </Button>
                    )}
                    {isRecording && (
                      <Button
                        variant="contained"
                        color="inherit"
                        startIcon={<StopIcon />}
                        onClick={stop}
                        sx={{ ...controlRowBtn, bgcolor: '#37474f', color: '#fff' }}
                      >
                        {t('voiceFeedback.stop')}
                      </Button>
                    )}
                    {(phase === 'ready' || phase === 'error') && (
                      <Button
                        variant="outlined"
                        onClick={handleNewMessage}
                        disabled={isBusy}
                        sx={{ ...outlinedLight, ...controlRowBtn }}
                      >
                        {t('voiceFeedback.rerecord')}
                      </Button>
                    )}
                  </Stack>
                  <Button
                    variant="contained"
                    startIcon={
                      submitPhase === 'loading' ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <SendIcon />
                      )
                    }
                    onClick={handleSubmit}
                    disabled={!canSubmit || submitPhase === 'loading'}
                    sx={{
                      minHeight: 44,
                      alignSelf: { xs: 'stretch', sm: 'auto' },
                      flexShrink: 0,
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    {submitPhase === 'loading' ? t('voiceFeedback.sending') : t('voiceFeedback.submit')}
                  </Button>
                </Stack>

                {phase === 'finalizing' && (
                  <Typography variant="caption" color="rgba(255,255,255,0.5)">
                    {t('voiceFeedback.finalizing')}
                  </Typography>
                )}

                {submitInfo ? <Alert severity="info">{submitInfo}</Alert> : null}
                {submitError ? <Alert severity="error">{submitError}</Alert> : null}
              </>
            )}
          </Stack>
        </Paper>
      </Stack>

      <Dialog
        open={isConsentOpen}
        onClose={handleConsentDecline}
        aria-labelledby="voice-ai-consent-title"
        PaperProps={{ sx: { bgcolor: '#1a2744' } }}
      >
        <DialogTitle id="voice-ai-consent-title">{t('voiceFeedback.aiConsent.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('voiceFeedback.aiConsent.body')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleConsentDecline}>{t('voiceFeedback.aiConsent.refuse')}</Button>
          <Button variant="contained" onClick={handleConsentAccept}>
            {t('voiceFeedback.aiConsent.accept')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
